import { useCallback, useEffect, useState } from 'react';
import { INPUT_TYPES } from '__components/Kizen/Input';
import { useTranslation } from 'react-i18next';
import TextInput from '__components/Inputs/TextInput';
import { useDebounce } from 'react-use';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { AssistantField } from '@kizenapps/engine';

export const NumberBlock = ({
  field,
  disabled = false,
}: {
  field: AssistantField;
  disabled?: boolean;
}) => {
  const {
    state,
    setState,
    evaluateExpression,
    shouldHideField,
    getFieldErrorState,
    registerFieldResetter,
    afterFieldChange,
    disabledKeys,
  } = useSetupAssistant();

  const value = (state as any)[field.key]?.value ?? field.default ?? '';
  const [internalValue, setInternalValue] = useState(value);

  const { t } = useTranslation();

  const isDisabled = disabledKeys.includes(field.key) || disabled;

  const setNumberValue = useCallback(
    (newValue: string) => {
      setState((prevState: any) => ({
        ...prevState,
        [field.key]: {
          type: 'number',
          value: newValue,
        },
      }));

      afterFieldChange(field.key);
    },
    [field.key, setState, afterFieldChange]
  );

  useEffect(() => {
    registerFieldResetter(field.key, () => {
      setNumberValue('');
      setInternalValue('');
    });
  }, [field.key, registerFieldResetter, setNumberValue]);

  useDebounce(
    () => {
      setNumberValue(internalValue);
    },
    DEFAULT_INPUT_DELAY,
    [internalValue]
  );

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  if (shouldHideField(field.key)) {
    return null;
  }

  const errorState = getFieldErrorState(field.key);

  const errorProps = {
    error: errorState?.message,
    validate: {
      message: errorState?.message,
      showMessage: errorState?.showMessage,
    },
  };

  return (
    <>
      <TextInput
        placeholder={field.default ?? field.placeholder ?? t('Enter a number')}
        value={internalValue}
        onChange={setInternalValue}
        label={field.label}
        labelInfo={field.tooltip}
        inModal
        type={INPUT_TYPES.NUMBER}
        {...errorProps}
        disabled={isDisabled}
        required={field.required}
      />
    </>
  );
};
